import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material';
import { geneKeyOf, newGeneRow, parseSymbolList } from './testRecordModel';
import { geneOfOption, resolveGeneSymbols, searchGenes } from './geneLookup';
import { useAutoTranslation } from 'src/hooks/useTranslate';

// "Add genes" on a Test's genes tested (TGCLOUD-1866, Phase 2 rework): one at a time from the gene
// lookup, or a pasted list, which is how a panel's gene list arrives (a lab's panel page, a report
// that could not be read).
//
// A PASTED LIST IS CHECKED, AND NOTHING IN IT IS LOST. Each symbol is looked up exactly
// (geneLookup.resolveGeneSymbols). What the lookup does not know is shown and, unless the
// clinician unticks it, added as typed with no gene id: a lab may print a symbol HGNC has since
// retired, and leaving it out would make a gene the lab tested read as untested. A symbol already
// on the Test, and a token that is not a gene symbol at all, are shown and not added.

const SECTION_SX = { mt: 1.5 };
const SECTION_TITLE_SX = { fontWeight: 700, fontSize: 13, mb: 0.5 };

function SymbolChips({ items, testid }) {
  return (
    <Box
      data-testid={testid}
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
    >
      {items.map((item) => (
        <Chip key={item} label={item} size="small" variant="outlined" />
      ))}
    </Box>
  );
}

function SearchTab({ listedKeys, pending, setPending }) {
  const t = useAutoTranslation();

  const [input, setInput] = React.useState('');
  const [options, setOptions] = React.useState([]);
  const [unavailable, setUnavailable] = React.useState(null);
  const latest = React.useRef(0);

  React.useEffect(() => {
    const term = input.trim();
    if (term.length < 3) {
      setOptions([]);
      return;
    }
    const id = (latest.current += 1);
    searchGenes(term).then((answer) => {
      if (latest.current !== id) return;
      setUnavailable(answer.unavailable);
      setOptions(answer.options);
    });
  }, [input]);

  const pendingKeys = new Set(pending.map((g) => g.gene.symbol));
  const choose = (option) => {
    if (!option) return;
    const gene = geneOfOption(option);
    const row = newGeneRow({
      symbol: gene.symbol,
      symbolAsPrinted: gene.symbol,
      hgncId: gene.hgncId,
      ncbiGeneId: gene.ncbiGeneId
    });
    if (
      !row.gene.symbol ||
      listedKeys.has(row.gene.symbol) ||
      pendingKeys.has(row.gene.symbol)
    ) {
      return;
    }
    setPending((prev) => [...prev, row]);
  };

  return (
    <Box>
      {unavailable && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {unavailable.message}
        </Alert>
      )}
      <Autocomplete
        options={options}
        filterOptions={(o) => o}
        getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
        getOptionDisabled={(o) => {
          const key = geneKeyOf(geneOfOption(o).symbol);
          return listedKeys.has(key) || pendingKeys.has(key);
        }}
        inputValue={input}
        value={null}
        onInputChange={(_, value, reason) => {
          if (reason !== 'reset') setInput(value);
        }}
        onChange={(_, option) => {
          choose(option);
          setInput('');
        }}
        noOptionsText={
          unavailable
            ? t('Gene lookup unavailable — this is not a result')
            : input.trim().length < 3
            ? t('Type at least 3 characters')
            : t('No matching genes')
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('Search for a gene')}
            placeholder={t('Search...')}
            fullWidth
          />
        )}
      />
      <Box sx={SECTION_SX}>
        <Typography sx={SECTION_TITLE_SX}>
          {t('To add ({{count}})', { count: pending.length })}
        </Typography>
        {pending.length ? (
          <Box
            data-testid="genes-to-add"
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
          >
            {pending.map((row) => (
              <Chip
                key={row.gene.symbol}
                label={row.gene.symbolAsPrinted}
                size="small"
                onDelete={() =>
                  setPending((prev) =>
                    prev.filter((r) => r.gene.symbol !== row.gene.symbol)
                  )
                }
              />
            ))}
          </Box>
        ) : (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('Choose genes from the search above.')}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function PasteTab({ listedKeys, checked, setChecked }) {
  const t = useAutoTranslation();

  const [text, setText] = React.useState('');
  const [checking, setChecking] = React.useState(false);

  const check = async () => {
    const tokens = parseSymbolList(text);
    const toLookUp = tokens
      .filter((t) => t.valid && !listedKeys.has(t.key))
      .map((t) => t.key);
    setChecking(true);
    try {
      const { results, unavailable } = await resolveGeneSymbols(toLookUp);
      const byKey = new Map(results.map((r) => [r.symbol, r]));
      setChecked({
        unavailable,
        rows: tokens.map((t) => {
          if (!t.valid) return { ...t, status: 'invalid', include: false };
          if (listedKeys.has(t.key)) {
            return { ...t, status: 'listed', include: false };
          }
          const found = byKey.get(t.key) || { status: 'unchecked' };
          // Found, or not found and kept unless the clinician unticks it: never dropped unseen.
          return { ...found, ...t, include: true };
        })
      });
    } finally {
      setChecking(false);
    }
  };

  const rows = checked?.rows || [];
  const of = (status) => rows.filter((r) => r.status === status);
  const toggle = (key, include) =>
    setChecked((prev) => ({
      ...prev,
      rows: prev.rows.map((r) => (r.key === key ? { ...r, include } : r))
    }));

  const keepList = (status, title, note, testid) =>
    of(status).length > 0 && (
      <Box sx={SECTION_SX} data-testid={testid}>
        <Typography sx={SECTION_TITLE_SX}>
          {title} ({of(status).length})
        </Typography>
        <Typography
          variant="caption"
          component="div"
          sx={{ color: 'text.secondary', mb: 0.5 }}
        >
          {note}
        </Typography>
        {of(status).map((r) => (
          <FormControlLabel
            key={r.key}
            sx={{ display: 'flex' }}
            control={
              <Checkbox
                size="small"
                checked={r.include}
                onChange={(e) => toggle(r.key, e.target.checked)}
              />
            }
            label={t('Add {{asTyped}} as typed', { asTyped: r.asTyped })}
          />
        ))}
      </Box>
    );

  return (
    <Box>
      <TextField
        label={t('Gene symbols')}
        multiline
        minRows={4}
        fullWidth
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setChecked(null);
        }}
        helperText={t(
          'Paste the genes the test covered, separated by commas, spaces or new lines.'
        )}
      />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <Button
          variant="outlined"
          onClick={check}
          disabled={checking || !text.trim()}
          startIcon={checking ? <CircularProgress size={14} /> : null}
        >
          {t('Check symbols')}
        </Button>
      </Box>
      {checked?.unavailable && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {t('{{message}} The symbols below could not be checked.', {
            message: checked.unavailable.message
          })}
        </Alert>
      )}
      {of('found').length > 0 && (
        <Box sx={SECTION_SX}>
          <Typography sx={SECTION_TITLE_SX}>
            {t('Found in the gene lookup (')}
            {of('found').length})
          </Typography>
          <SymbolChips
            testid="paste-found"
            items={of('found').map((r) => r.approved || r.key)}
          />
        </Box>
      )}
      {keepList(
        'not-found',
        t('Not found in the gene lookup'),
        t(
          'Check the spelling. Kept as typed, with no gene id, unless you untick it.'
        ),
        'paste-not-found'
      )}
      {keepList(
        'unchecked',
        t('Not checked'),
        t('Kept as typed, with no gene id, unless you untick it.'),
        'paste-unchecked'
      )}
      {of('listed').length > 0 && (
        <Box sx={SECTION_SX}>
          <Typography sx={SECTION_TITLE_SX}>
            {t('Already on this Test (')}
            {of('listed').length})
          </Typography>
          <SymbolChips
            testid="paste-listed"
            items={of('listed').map((r) => r.key)}
          />
        </Box>
      )}
      {of('invalid').length > 0 && (
        <Box sx={SECTION_SX}>
          <Typography sx={SECTION_TITLE_SX}>
            {t('Not gene symbols (')}
            {of('invalid').length})
          </Typography>
          <SymbolChips
            testid="paste-invalid"
            items={of('invalid').map((r) => r.asTyped)}
          />
        </Box>
      )}
    </Box>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {(rows: object[]) => void} props.onAdd  new genesTested rows, none already listed
 * @param {Set<string>} props.listedKeys  the gene keys the Test already lists
 */
export default function AddGenesDialog({ open, onClose, onAdd, listedKeys }) {
  const t = useAutoTranslation();

  const [tab, setTab] = React.useState('search');
  const [pending, setPending] = React.useState([]);
  const [checked, setChecked] = React.useState(null);

  React.useEffect(() => {
    if (!open) {
      setTab('search');
      setPending([]);
      setChecked(null);
    }
  }, [open]);

  const pasted = (checked?.rows || [])
    .filter((r) => r.include)
    .map((r) =>
      newGeneRow({
        symbol: r.key,
        symbolAsPrinted: r.asTyped,
        hgncId: r.hgncId,
        ncbiGeneId: r.ncbiGeneId
      })
    );
  const rows = tab === 'search' ? pending : pasted;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="add-genes-title"
    >
      <DialogTitle id="add-genes-title" sx={{ pb: 1 }}>
        {t('Add genes tested')}
      </DialogTitle>
      <DialogContent dividers>
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          sx={{ mb: 1.5 }}
          aria-label={t('How to add genes')}
        >
          <Tab value="search" label={t('Search')} />
          <Tab value="paste" label={t('Paste a list')} />
        </Tabs>
        {tab === 'search' ? (
          <SearchTab
            listedKeys={listedKeys}
            pending={pending}
            setPending={setPending}
          />
        ) : (
          <PasteTab
            listedKeys={listedKeys}
            checked={checked}
            setChecked={setChecked}
          />
        )}
        <Typography
          variant="caption"
          component="div"
          sx={{ color: 'text.secondary', mt: 1.5 }}
        >
          {t(
            'Each gene is added as the whole gene, with nothing stated about its coverage or methods. Edit a gene to record what the laboratory states.'
          )}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" color="secondary">
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={!rows.length}
          onClick={() => onAdd(rows)}
        >
          {rows.length === 1
            ? t('Add 1 gene')
            : t('Add {{count}} genes', { count: rows.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
